import type { UserService } from "@common/services/user-service";
import type { UserPreferences } from "@common/types/user-preferences";
import { makeAutoObservable, runInAction } from "mobx";

interface UserPreferencesStoreDependencies {
  localStorage: {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
    removeItem: (key: string) => Promise<void>;
  };
  userService: UserService;
}

export const USER_PREFERENCES_CACHE_KEY = "USER_PREFERENCES_CACHE";

export default class UserPreferencesStore {
  deps: UserPreferencesStoreDependencies;

  userPreferences: Partial<Omit<UserPreferences, "userId">> | null = null;

  constructor(dependencies: UserPreferencesStoreDependencies) {
    makeAutoObservable(this);

    this.deps = dependencies;
  }

  async load(userId: string) {
    const cachedState = await this.deps.localStorage.getItem(
      USER_PREFERENCES_CACHE_KEY
    );

    if (cachedState) {
      this.userPreferences = JSON.parse(cachedState);
    }

    try {
      const userPreferences = await this.deps.userService.getUserPreferences(
        userId
      );

      runInAction(() => {
        this.userPreferences = userPreferences;
      });
    } catch (error) {
      // Fails when offline
      console.error(error);
    }
  }

  async update(
    userId: string,
    preferences: Partial<Omit<UserPreferences, "userId">>
  ) {
    await this.deps.localStorage.setItem(
      USER_PREFERENCES_CACHE_KEY,
      JSON.stringify(preferences)
    );

    await this.deps.userService.updatePreferences(userId, preferences);
    runInAction(() => {
      this.userPreferences = { ...this.userPreferences, ...preferences };
    });
  }
}
